import React, { useState, useRef, useEffect } from "react";
import {
  Wand2, X, Send, Loader2, Copy, Check, AlertTriangle,
  Bot, User, RotateCcw, Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { flowHelperAsk } from "./api";

const SUGGESTIONS = [
  "First welcome message should be friendlier",
  "Send a nudge after 3 hours if no reply",
  "Change Day 3 follow-up text",
  "Stop AI if lead says 'lawyer'",
  "Add new plan to knowledge base",
];

// Inline markdown-lite: **bold** only
function boldify(text) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i}>{p.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}

function StepItem({ step, onCopy, copiedIdx, idx }) {
  return (
    <div className="fh-step" data-testid={`fh-step-${step.num || idx}`}>
      <span className="fh-step-num">{step.num || idx + 1}</span>
      <div className="fh-step-body">
        <div className="fh-step-text">{boldify(step.text || "")}</div>
        {step.paste && (
          <div className="fh-step-paste">
            <pre>{step.paste}</pre>
            <button
              className="fh-copy-btn"
              onClick={() => onCopy(step.paste, step.num || idx + 1)}
              data-testid={`fh-copy-${step.num || idx}`}
              title="Copy to clipboard"
            >
              {copiedIdx === (step.num || idx + 1) ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copiedIdx === (step.num || idx + 1) ? "Copied!" : "Copy"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function FlowHelper() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, open]);

  const handleCopy = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    } catch {}
  };

  const ask = async (userText) => {
    const text = (userText || input || "").trim();
    if (!text || sending) return;
    setInput("");
    const userMsg = { role: "user", content: text };
    const nextMsgs = [...messages, userMsg];
    setMessages(nextMsgs);
    setSending(true);
    try {
      const history = nextMsgs.slice(0, -1).map((m) => ({ role: m.role, content: m.content || m.message }));
      const r = await flowHelperAsk(text, history);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          message: r.message || "",
          steps: r.steps || [],
          needsAdmin: !!r.needsAdmin,
          adminRequest: r.adminRequest || "",
          _raw: r._unparsed ? r.message : null,
        },
      ]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", message: "⚠️ " + (e.message || "Failed"), error: true }]);
    } finally {
      setSending(false);
    }
  };

  const resetChat = () => {
    setMessages([]);
    setInput("");
  };

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          className="fh-fab"
          onClick={() => setOpen(true)}
          data-testid="flow-helper-open"
          title="Ask Flow Helper — say what you want to change and it'll tell you exactly where"
        >
          <Wand2 className="w-5 h-5" />
          <span>Flow Helper</span>
          <span className="fh-fab-badge">AI</span>
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div className="fh-drawer" data-testid="flow-helper-drawer" role="dialog">
          <div className="fh-header">
            <div className="fh-header-left">
              <div className="fh-header-icon">
                <Wand2 className="w-4 h-4" />
              </div>
              <div>
                <strong>Flow Helper</strong>
                <span>Tell me what to change — I'll show you exactly where</span>
              </div>
            </div>
            <div className="fh-header-actions">
              {messages.length > 0 && (
                <button className="fh-icon-btn" onClick={resetChat} title="New chat" data-testid="fh-reset">
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}
              <button className="fh-icon-btn" onClick={() => setOpen(false)} title="Close" data-testid="fh-close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="fh-body" ref={scrollRef}>
            {messages.length === 0 && (
              <div className="fh-empty">
                <div className="fh-empty-icon"><Sparkles className="w-8 h-8" /></div>
                <h4>Ask me to change any WhatsApp rule</h4>
                <p>I know every tab, field and variable in the Automation Studio. Just describe what you want — I'll give you the exact steps or tell you to ping admin.</p>
                <div className="fh-suggest-list">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      className="fh-suggest-btn"
                      onClick={() => ask(s)}
                      data-testid={`fh-suggest-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`fh-msg fh-msg-${m.role}`} data-testid={`fh-msg-${i}`}>
                <div className="fh-msg-avatar">
                  {m.role === "user" ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                </div>
                <div className="fh-msg-body">
                  {m.role === "user" ? (
                    <div className="fh-msg-text">{m.content}</div>
                  ) : (
                    <>
                      {m.message && (
                        <div className={`fh-msg-text ${m.error ? "fh-error" : ""}`}>{boldify(m.message)}</div>
                      )}
                      {m.needsAdmin && m.adminRequest && (
                        <div className="fh-admin-card" data-testid={`fh-admin-${i}`}>
                          <div className="fh-admin-head">
                            <AlertTriangle className="w-4 h-4" />
                            <strong>Escalate to admin</strong>
                            <button
                              className="fh-copy-btn"
                              onClick={() => handleCopy(m.adminRequest, `admin-${i}`)}
                              data-testid={`fh-admin-copy-${i}`}
                            >
                              {copiedIdx === `admin-${i}` ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                              {copiedIdx === `admin-${i}` ? "Copied!" : "Copy message"}
                            </button>
                          </div>
                          <div className="fh-admin-body">{m.adminRequest}</div>
                        </div>
                      )}
                      {m.steps && m.steps.length > 0 && (
                        <div className="fh-steps">
                          {m.steps.map((s, idx) => (
                            <StepItem
                              key={idx}
                              step={s}
                              idx={idx}
                              onCopy={handleCopy}
                              copiedIdx={copiedIdx}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}

            {sending && (
              <div className="fh-msg fh-msg-assistant">
                <div className="fh-msg-avatar"><Bot className="w-3.5 h-3.5" /></div>
                <div className="fh-msg-body">
                  <div className="fh-typing"><span /><span /><span /></div>
                </div>
              </div>
            )}
          </div>

          <div className="fh-input-row">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. send a nudge after 3 hours if no reply"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
              disabled={sending}
              data-testid="fh-input"
            />
            <Button
              onClick={() => ask()}
              disabled={sending || !input.trim()}
              className="fh-send-btn"
              data-testid="fh-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
